import {
  DeleteTreeNodesRequest,
  PasteFolderTaskResponseDTO,
  PasteOperationType,
  PasteResponseDTO,
  TreeNodeDTO,
  TreeNodeExtenededResponseMessageDTO,
} from 'business/models/Models';
import SynchTripService from 'business/services/SynchrotronTripService';
import SystemTaskService from 'business/services/systemTaskService';
import { isModalOpen, RMModalProps, useModalHandlers } from 'components/modal';
import { PRODUCT_SWITCH_MODAL_ID } from 'constants/messages/productSwitch';
import { LongRunningTask } from 'components/systemTasks/SystemTask';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import React, { useCallback } from 'react';
import { useRMDispatch } from 'redux/hooks';
import { addTask } from 'redux/reducers/systemTaskReducer';
import { getNewId } from 'utils/Global';
import ExperimentService from 'business/services/ExperimentService';
import DeleteModalConfirmationBody from './modals/deleteModalConfirmationBody';
import DeleteResponseModalBody from './modals/deleteResponseModalBody';
import DeleteConfirmationModal from './modals/deleteConfirmationModal';
import { TreeService } from 'business/services/TreeService';
import PasteModalConfirmationBody from './modals/pasteModalConfirmationBody';
import ETreeActionTypes from 'enums/ETreeActionTypes';
import PasteResponseModalBody from './modals/pasteResponseModalBody';
import { DELETE_CONFIRMATION_MESSAGES, TreeNodeDetail } from './constants';
import { copyPasteValidation } from './utils';
import { EPlateState } from 'enums/EPlatestate';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import { useSystemState } from 'redux/hooks/common';
import { SystemState } from 'redux/reducers/commonReducer';
import { FmlxButton } from 'fmlx-common-ui';

const synchTripService = new SynchTripService();
const experimentService = new ExperimentService();
const experimentAndContainerNodeTypes = [
  ETreeNodeTypes.Experiment,
  ETreeNodeTypes.Project,
  ETreeNodeTypes.ProjectFolder,
  ETreeNodeTypes.ProjectsFolder,
];
const containerNodeTypes = [
  ETreeNodeTypes.Project,
  ETreeNodeTypes.ProjectFolder,
  ETreeNodeTypes.ProjectsFolder,
];
const useDeleteNodes = () => {
  const { showModal, showLoadingModal, hideModal } = useModalHandlers();
  return async (
    nodesToDelete: TreeNodeDetail[],
    postDeletionAction: (
      deleteNodeIds: number[],
      undeletedNodes: TreeNodeExtenededResponseMessageDTO[],
    ) => void,
  ) => {
    showLoadingModal('Deleting');
    const req = new DeleteTreeNodesRequest();
    req.treeNodeId = nodesToDelete.map((node) => node.treeNodeId);
    const deleteResponse = await new TreeService().deleteTreeNodes(req);
    const allDeleteNodes = deleteResponse.flatMap((item) => item.deletedNodes);
    const allUnDeletedNodes = deleteResponse
      .flatMap((item) => item.unDeletedNodes)
      .distinctBy('id');
    hideModal();
    if (allUnDeletedNodes.length) {
      showModal({
        body: (
          <DeleteResponseModalBody
            deleteTreeNodes={allDeleteNodes}
            allUnDeletedNodes={allUnDeletedNodes}
          />
        ),
        heading: 'Confirmation',
        noButtonText: 'Close',
        isCancelOnly: true,
      });
    }
    if (postDeletionAction) {
      postDeletionAction(
        allDeleteNodes.map((n) => n.id),
        allUnDeletedNodes,
      );
    }
  };
};

export const useCheckNodeAvailability = () => {
  const { showModal, showLoadingModal, hideModal } = useModalHandlers();
  return async (
    nodesToDelete: TreeNodeDetail[],
    postDeleteAction: (
      deleteNodeIds: number[],
      undeletedNodes: TreeNodeExtenededResponseMessageDTO[],
    ) => void,
  ) => {
    showLoadingModal('Checking node availability');
    const nodeIds = nodesToDelete.map((node) => node.treeNodeId);
    const response = await new TreeService().getDeletedNodeAvailability(nodeIds);
    const deletedNodes = response.deletedNodes ?? [];
    const undeletedNodes = (response.unDeletedNodes ?? []).distinctBy('id');
    const onlyUndeleteNode = deletedNodes.length === 0;
    hideModal();
    showModal({
      body: (
        <DeleteConfirmationModal
          deletedNodes={deletedNodes}
          undeletedNodes={undeletedNodes}
        />
      ),
      heading: DELETE_CONFIRMATION_MESSAGES.DELETE_MODAL_TITLE,
      okButtonText: 'DELETE',
      onOk: async () => {
        hideModal();
        showLoadingModal('Deleting');
        const req = new DeleteTreeNodesRequest();
        req.treeNodeId = deletedNodes.map((node) => node.id);
        await new TreeService().deleteTreeNodes(req);
        hideModal();

        if (postDeleteAction) {
          postDeleteAction(
            deletedNodes.map((node) => node.id),
            undeletedNodes,
          );
        }
      },
      useDefaultButtonLayout: true,
      noButtonType: FmlxButton.Type.BASIC,
      isCancelOnly: onlyUndeleteNode,
      noButtonText: onlyUndeleteNode ? 'Close' : 'Cancel',
    });
  };
};

export const useDeleteNodesHandler = () => {
  const systemState = useSystemState();
  const { showLoadingModal, showModal, hideModal } = useModalHandlers();
  const deleteNodes = useDeleteNodes();
  let showDetailedDeleteModal = false;
  return async (
    nodes: TreeNodeDetail[],
    postDeletionAction: (
      deleteNodeIds: number[],
      undeletedNodes: TreeNodeExtenededResponseMessageDTO[],
    ) => void = null,
  ) => {
    if (systemState !== SystemState.IDLE) {
      return;
    }
    showLoadingModal('Please wait');
    const unableToDeleteNodes = [];
    const nodesToDelete = [];
    let deleteTrips = false;
    for (const node of nodes) {
      let isNodeUnableToDelete = false;

      if (node.type === ETreeNodeTypes.SynchrotronTripv4) {
        const crystalCount = await synchTripService.getSynchTripCrystalCount(
          node.entityId,
        );

        if (crystalCount && (node?.extra?.status === 'Open' || !crystalCount)) {
          unableToDeleteNodes.push(node);
          isNodeUnableToDelete = true;
        } else {
          deleteTrips = true;
        }
      }

      if (!isNodeUnableToDelete) {
        node?.extra?.plates?.forEach((plate) => {
          if (plate?.plateState >= EPlateState.Imaged) {
            showDetailedDeleteModal = true;
          }
        });
        nodesToDelete.push(node);
      }
    }
    const experimentIds = nodesToDelete
      .filter(
        (node: TreeNodeDTO) => node.type === ETreeNodeTypes.Experiment && node.extra?.id,
      )
      .map((node: TreeNodeDTO) => node.extra?.id);

    const isAnyContainerNode = nodesToDelete.some((node: TreeNodeDTO) =>
      containerNodeTypes.includes(node.type as ETreeNodeTypes),
    );
    let imagesCount = 0;
    if (experimentIds.length && !isAnyContainerNode) {
      imagesCount = await experimentService.getExperimentsTotalImagesCount(experimentIds);
    }
    const isAnyExperimentAndContainerNode = nodesToDelete.some((node: TreeNodeDTO) =>
      experimentAndContainerNodeTypes.includes(node.type as ETreeNodeTypes),
    );

    hideModal();
    showModal({
      okButtonText: unableToDeleteNodes.length > 0 ? 'OK' : 'Yes',
      heading: 'Confirmation',
      body: (
        <DeleteModalConfirmationBody
          nodesToDelete={nodesToDelete}
          unableToDeleteNodes={unableToDeleteNodes}
          deleteTrips={deleteTrips}
          isAnyExperimentAndContainerNode={isAnyExperimentAndContainerNode}
          isAnyContainerNode={isAnyContainerNode}
          imagesCount={imagesCount}
          showDetailedDeleteModal={isAnyContainerNode || showDetailedDeleteModal}
        />
      ),
      onOk: () => {
        hideModal();
        if (unableToDeleteNodes.length <= 0) {
          deleteNodes(nodesToDelete, postDeletionAction);
        }
      },
      isOkOnly: !!unableToDeleteNodes.length,
    });
  };
};
const postPasteOperation = (
  response: PasteResponseDTO,
  postPasteAction: (response: PasteResponseDTO) => void = null,
  hideModal: () => void,
  showModal?: (props: Omit<RMModalProps, 'showModal' | 'setShowModal'>) => void,
) => {
  if (postPasteAction && response.pastedNodes?.length) {
    postPasteAction(response);
  }
  if (!isModalOpen(PRODUCT_SWITCH_MODAL_ID)) {
    hideModal();
  }

  if (showModal) {
    showModal({
      body: <PasteResponseModalBody response={response} />,
      heading: 'Alert',
      noButtonText: 'Close',
      isCancelOnly: true,
    });
  }
};
export const usePasteTreeNodes = () => {
  const { showModal, showLoadingModal, hideModal } = useModalHandlers();
  const dispatch = useRMDispatch();
  return useCallback(
    async (
      operationType,
      nodesToPaste: TreeNodeDTO[],
      selectedExplorerNode: TreeNodeDTO,
      postPasteAction: (response: PasteResponseDTO) => void = null,
      showCompleteAlert = true,
      showLoadingAlert,
    ) => {
      if (showLoadingAlert) {
        showLoadingModal(
          operationType === ETreeActionTypes.Copy ? 'Pasting...' : 'Moving...',
        );
      }
      const task = {
        id: getNewId(),
        title: `${operationType === ETreeActionTypes.Copy ? 'Copying' : 'Moving'} to '${
          selectedExplorerNode?.name
        }'`,
        completionTitle: `${
          operationType === ETreeActionTypes.Copy ? 'Copied' : 'Moved'
        } to '${selectedExplorerNode?.name}'`,
        param: [
          nodesToPaste.map((node) => node.id),
          selectedExplorerNode?.id,
          operationType === ETreeActionTypes.Copy
            ? PasteOperationType.Copy
            : PasteOperationType.Cut,
        ],
        serviceMathod: new SystemTaskService().copyPasteFolder,
        onComplete: (response: PasteFolderTaskResponseDTO) => {
          postPasteOperation(
            response.pasteResponse,
            postPasteAction,
            hideModal,
            showCompleteAlert || response.pasteResponse?.unableToPasteNodes?.length
              ? showModal
              : null,
          );
          return !(response.pasteResponse?.unableToPasteNodes?.length > 0);
        },
      } as LongRunningTask;
      dispatch(addTask(task));
    },
    [],
  );
};

export const usePasteNodesHandler = () => {
  const { showModal, hideModal } = useModalHandlers();
  const pasteTreeNodes = usePasteTreeNodes();
  return useCallback(
    async (
      operationType: ETreeActionTypes,
      nodesToCopy: TreeNodeDTO[],
      selectedExplorerNode: TreeNodeDTO,
      postPasteAction: (response: PasteResponseDTO) => void = null,
      showCompleteAlert = true,
      showLoadingModal = true,
    ) => {
      const gpService = new GlobalPreferencesService();
      const result: any = await gpService.getUserProperty('Tree.ConfirmMove');
      const confirmMoveDialog = result.value;
      const { copyNodes, unableToPasteNodes } = copyPasteValidation(
        nodesToCopy,
        selectedExplorerNode,
      );
      if (unableToPasteNodes.length === 0 && !confirmMoveDialog) {
        hideModal();
        pasteTreeNodes(
          operationType,
          copyNodes,
          selectedExplorerNode,
          postPasteAction,
          showCompleteAlert,
          showLoadingModal,
        );
      } else {
        showModal({
          heading: copyNodes?.length ? 'Confirmation' : 'Error',
          body: (
            <PasteModalConfirmationBody
              nodesToCopy={copyNodes}
              unableToPasteNodes={unableToPasteNodes}
              targetNode={selectedExplorerNode}
              actionType={operationType}
            />
          ),
          onOk: () => {
            hideModal();
            if (nodesToCopy.length) {
              pasteTreeNodes(
                operationType,
                copyNodes,
                selectedExplorerNode,
                postPasteAction,
                showCompleteAlert,
                showLoadingModal,
              );
            }
          },
          isCancelOnly: copyNodes?.length === 0,
          showFooterSeparator: true,
          headerStyle: { color: '#1B1B1B' },
        });
      }
    },
    [pasteTreeNodes],
  );
};
